import React from 'react'
import { GridColumnsPanelProps } from '@mui/x-data-grid/components'
import {
    gridColumnLookupSelector,
    gridColumnVisibilityModelSelector,
    useGridApiContext,
    useGridSelector,
} from '@mui/x-data-grid'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Checkbox from '@mui/material/Checkbox'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { IssueGridCols } from '@finance-lab/utils/types'
import { useTranslation } from 'react-i18next'

export type CustomColumnsPanelProps<T extends string = IssueGridCols> = GridColumnsPanelProps & {
    onColumnVisibilityChange: (settings: Partial<Record<T, boolean>>) => void
    onResetColumns: () => void
}

export const CustomColumnsPanel = <T extends string = IssueGridCols>(props: CustomColumnsPanelProps<T>) => {
    const apiRef = useGridApiContext()
    const { t } = useTranslation()

    const columnLookup = useGridSelector(apiRef, gridColumnLookupSelector)
    const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector)

    const toggleColumn = (field: string, checked: boolean) => {
        const visibileColumns = apiRef.current.getVisibleColumns()
        const model = apiRef.current
            .getAllColumns()
            .reduce((acc, col) => ({ ...acc, [col.field]: visibileColumns.some((vc) => vc.field === col.field) }), {})

        props.onColumnVisibilityChange({
            ...model,
            [field]: checked,
        })
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 4, py: 4, maxHeight: '300px', overflow: 'auto' }}>
                {Object.keys(columnVisibilityModel).map((field) => (
                    <Stack direction='row' key={field}>
                        <FormControlLabel
                            control={
                                <Checkbox checked={columnVisibilityModel[field] !== false} size='small' sx={{ p: 1 }} />
                            }
                            label={columnLookup[field]?.headerName ?? field}
                            onChange={(_, newValue) => toggleColumn(field, newValue)}
                        />
                    </Stack>
                ))}
            </Box>

            <Stack direction={'row'} sx={{ alignSelf: 'flex-end' }}>
                <Button
                    sx={{ m: 2 }}
                    size='small'
                    variant='outlined'
                    onClick={() => {
                        props.onResetColumns()
                    }}
                >
                    {t('components.customColumnsPanel.reset')}
                </Button>
                <Button
                    sx={{ m: 2 }}
                    size='small'
                    variant='outlined'
                    onClick={() => {
                        apiRef.current.hideFilterPanel()
                    }}
                >
                    OK
                </Button>
            </Stack>
        </Box>
    )
}
